import React, { useEffect, useState } from 'react';
import { NSButton, NSCard, NSLoadingScreen } from 'bricks';
import NSSearchBar from 'bricks/NSSearchBar/NSSearchBar';
import { Col, Row } from 'reactstrap';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useIntersectionObserver } from 'usehooks-ts';
import RenderIf from 'components/RenderIf/RenderIf';
import { URLS } from 'services/urls';
import { Link } from 'react-router-dom';
import ActivityGroup from './ActivityGroup';
import getCRMDealActivitiesInfiniteQueryOptions from './hooks/useGetCRMDealActivities';
import { IActivity } from '../types';
import AddNote from './AddNote/AddNote';
import getCRMIntegrationStatusQueryOptions from './hooks/useGetCRMIntegrations';

export interface IActivityListProps {
    organizationId: string;
    dealId: number;
}

const ActivityList = ({ organizationId, dealId }: IActivityListProps) => {
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [searchText, setSearchText] = React.useState<string>('');
    const {
        data,
        fetchNextPage,
        isFetchingNextPage,
        isSuccess,
        isLoading,
    } = useInfiniteQuery(getCRMDealActivitiesInfiniteQueryOptions(organizationId, dealId));

    const activities = data?.pages.flatMap(page => page.items) || [];

    const { isIntersecting, ref: intersectionObserverRef } = useIntersectionObserver({
        threshold: 0.5,
    });

    useEffect(() => {
        if (isIntersecting && !isFetchingNextPage && isSuccess) {
            fetchNextPage();
        }
    }, [isIntersecting, isFetchingNextPage, isSuccess, fetchNextPage]);

    const groupedActivities = activities.reduce((acc, activity) => {
        const date = new Date(activity.activityDate);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });

        if (!acc[monthKey]) {
            acc[monthKey] = {
                label: monthLabel,
                activities: [],
            };
        }
        acc[monthKey].activities.push(activity);
        return acc;
    }, {} as Record<string, { label: string; activities: IActivity[] }>);

    const currentMonthKey = `${new Date().getFullYear()}-${new Date().getMonth()}`;
    const hasCurrentMonthActivities = Boolean(groupedActivities[currentMonthKey]);

    const { data: integrationStatus } = useQuery(getCRMIntegrationStatusQueryOptions(organizationId));
    const showIntegrationBanner = !!integrationStatus && !(integrationStatus.google || integrationStatus.microsoft);

    return (
        <>
            {showIntegrationBanner && (
                <Row className="d-flex justify-content-between align-items-center px-3 pt-3">
                    <Col className="p-0" xs="12">
                        <NSCard
                            className="NSCard__left-border--info shadow-none p-3 d-flex flex-row justify-content-between align-items-center mb-0"
                        >
                            <div>
                                <small className="text-uppercase text-primary">
                                    Account setup
                                </small>
                                <h4 className="text-white my-0">
                                    Integrate your email account with Northspyre Deal
                                </h4>
                                <p className="mb-0">
                                    Connect your Google or Outlook email to automatically log deal-related communications using associated contacts.
                                </p>
                            </div>
                            <Link
                                to={URLS.PROJECTS_APP.USER_SETTINGS}
                                target="_blank"
                                rel="noreferrer"
                                className="cursor-pointer"
                            >
                                <NSButton
                                    text="Set up"
                                    color="primary"
                                    outline={false}
                                />
                            </Link>
                        </NSCard>
                    </Col>
                </Row>
            )}
            <Row className="d-flex justify-content-between align-items-center p-3">
                <Col xs="7" className="p-0">
                    <NSButton
                        text="Filter activities"
                        icon="filter"
                        className="NSButton--secondary-lighten"

                    />
                </Col>
                <Col xs="5" className="d-flex align-items-center justify-content-end p-0">
                    <NSSearchBar
                        placeholder="Search"
                        searchValue={searchText}
                        handleChangeSearchValue={e => setSearchText(e.target.value)}
                        containerClassName="m-0 flex-grow-1"
                    />
                    <NSButton
                        text="Add note"
                        outline
                        color="primary"
                        className="ml-2"
                        disabled={addNoteOpen}
                        callback={() => setAddNoteOpen(true)}
                    />
                </Col>
            </Row>
            <Row className="d-flex flex-column">
                <RenderIf isTrue={addNoteOpen}>
                    <AddNote
                        dealId={dealId}
                        orgId={organizationId}
                        onClose={() => setAddNoteOpen(false)}
                        showMonthHeader={!hasCurrentMonthActivities}
                    />
                </RenderIf>

                {Object.entries(groupedActivities || {})
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([monthKey, { label, activities }], idx, array) => (
                        <div
                            key={monthKey}
                            className="w-100 px-3 pb-2"
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="font-weight-bold text-nowrap text-uppercase">
                                    {label}
                                </h5>
                                <hr className="ml-2 w-100" />
                            </div>
                            <ActivityGroup
                                organizationId={organizationId}
                                dealId={dealId}
                                activities={activities}
                            />

                            {idx === array.length - 1 && (
                                <div ref={intersectionObserverRef} className="w-100">
                                    {(isLoading || isFetchingNextPage) && (
                                        <NSLoadingScreen />
                                    )}
                                </div>
                            )}

                        </div>
                    ))}
            </Row>
        </>
    );
};
export default ActivityList;
